import { FC, MouseEvent } from 'react';
import { UseFieldArrayAppend } from 'react-hook-form';
import styled, { css } from 'styled-components';

import Text from '@/components/Text';
import { VideoCompilerCompilation } from '@/types/resources';
import { humanFileSize } from '@/utils/helpers';

import { FormDataT, Part } from '..';

const Icon = styled.div`
  width: 32px;
  height: 32px;
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
`;

const RemoveIcon = styled(Icon)`
  background: rgb(248, 248, 248);
  svg {
    color: ${({ theme }) => theme.colors.gray};
  }

  &:hover {
    background: #fff4f2;
    svg {
      color: ${({ theme }) => theme.colors.error};
    }
  }
`;

const AddIcon = styled(Icon)`
  background: rgb(242, 241, 252);
  svg {
    color: ${({ theme }) => theme.colors.purple};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.purple};
    svg {
      color: ${({ theme }) => theme.colors.white};
    }
  }
`;

const Card = styled.div<{ $hoverable: boolean; $clickable?: boolean }>`
  display: flex;
  gap: 16px;
  padding: 8px;
  border: 1px solid rgba(242, 243, 247, 1);
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  align-items: center;

  &:hover {
    ${({ $hoverable }) =>
      $hoverable &&
      css`
        background: rgba(251, 251, 251, 1);
      `}
  }
  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
    `}
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Info = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Link = styled(Text)`
  display: flex;
  align-items: center;
  gap: 4px;
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.colors.purple};
  }
`;

const Heading = styled(Text)`
  display: flex;
  align-items: center;
  gap: 10px;
`;

interface TypeProps {
  $partType: 'tip' | 'version' | 'compilation';
}

const TypeBadge = styled(Text)<TypeProps>`
  padding: 2px 4px;
  border-radius: 4px;
  text-transform: capitalize;
  display: inline-block;

  ${({ $partType }) =>
    $partType === 'compilation' &&
    css`
      color: #ad64c5;
      background: #f9f3fb;
    `}
  ${({ $partType }) =>
    $partType === 'version' &&
    css`
      color: #c9513d;
      background: #fbf1f0;
    `}
  ${({ $partType }) =>
    $partType === 'tip' &&
    css`
      color: #18713d;
      background: #edf4f0;
    `}
`;

interface Props {
  part: Part;
  append?: UseFieldArrayAppend<FormDataT, 'parts'>;
  remove?: VoidFunction;
  compilationType?: string;
}

const FileCard: FC<Props> = ({ part, append, remove, compilationType }) => {
  const title = part.source === 'drive' ? part.fileData.name : part.model.title;
  const size =
    part.source === 'drive'
      ? part.fileData.size
      : part.model.video?.size ?? part.model.fileData!.size;
  const fileUrl =
    part.source === 'drive'
      ? part.fileData.link
      : part.model.video?.versions?.original ?? part.model.fileData!.link;

  const onClick = () => {
    if (
      compilationType === 'version' &&
      part.type === 'version' &&
      part.source === 'local'
    ) {
      part.model.parts!.forEach((part) =>
        append?.({
          type: 'tip',
          source: 'drive',
          partId: part.model.fileData!.id,
          fileData: part.model.fileData!,
        }),
      );

      return;
    }

    if (
      compilationType === 'compilation' &&
      part.type === 'compilation' &&
      part.source === 'local'
    ) {
      part.model.parts!.forEach((part) =>
        append?.({
          type: 'version',
          partId: part.id,
          source: 'local',
          model: part.model as Omit<VideoCompilerCompilation, 'parts'>,
        }),
      );

      return;
    }

    append?.(part);
  };

  return (
    <Card
      $hoverable
      onClick={append ? onClick : undefined}
      $clickable={!!append}
    >
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="40" height="40" rx="4" fill="#F8F8F8" />
        <path
          d="M11.875 14.6875H21.875C22.538 14.6875 23.1739 14.9509 23.6428 15.4197C24.1116 15.8886 24.375 16.5245 24.375 17.1875V24.6875C24.375 24.8533 24.3092 25.0122 24.1919 25.1294C24.0747 25.2467 23.9158 25.3125 23.75 25.3125H13.75C13.087 25.3125 12.4511 25.0491 11.9822 24.5803C11.5134 24.1114 11.25 23.4755 11.25 22.8125V15.3125C11.25 15.1467 11.3158 14.9878 11.4331 14.8706C11.5503 14.7533 11.7092 14.6875 11.875 14.6875V14.6875Z"
          stroke="#909090"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M24.375 18.75L28.75 16.25V23.75L24.375 21.25"
          stroke="#909090"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <Content>
        <Heading $type="smallBold">
          {title}
          <TypeBadge $type="extraSmall" $partType={part.type} as="span">
            {part.type}
          </TypeBadge>
        </Heading>
        <Info>
          <Text $type="extraSmall" $color="gray">
            {humanFileSize(size!)}
          </Text>
          <Link
            $type="extraSmall"
            as="a"
            href={fileUrl}
            target="_blank"
            onClick={(e: MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="14" height="14" fill="#F8F8F8" />
              <path
                d="M5.69531 8.30617L8.30617 5.69531"
                stroke="#909090"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.65328 8.95851L6.56542 10.0464C6.21918 10.3925 5.74963 10.5869 5.26006 10.5869C4.77048 10.5868 4.30097 10.3923 3.95479 10.0461C3.60861 9.69996 3.4141 9.23045 3.41406 8.74087C3.41402 8.2513 3.60844 7.78175 3.95457 7.43551L5.04242 6.34766"
                stroke="#909090"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.95851 7.65328L10.0464 6.56542C10.3925 6.21918 10.5869 5.74963 10.5869 5.26006C10.5868 4.77048 10.3923 4.30097 10.0461 3.95479C9.69996 3.60861 9.23045 3.4141 8.74088 3.41406C8.2513 3.41402 7.78176 3.60844 7.43551 3.95456L6.34766 5.04242"
                stroke="#909090"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            File link
          </Link>
        </Info>
      </Content>
      {!!remove && (
        <RemoveIcon onClick={remove}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 3.99951L4 11.9995"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 11.9995L4 3.99951"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </RemoveIcon>
      )}
      {!!append && (
        <AddIcon>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.0006 4.34315V15.6569"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15.6575 10H4.34375"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </AddIcon>
      )}
    </Card>
  );
};

export default FileCard;
